// Feature Toggle Store - Polling and Fetch Functions
// Shared fetch logic and polling mechanism for feature toggles

// ============================================================================
// TYPES
// ============================================================================

// `null` is a toggle the API could not answer (HTTP error, 429, network) and no earlier answer
// covers. It is not `false`: every consumer that merges toggles must skip it, or a failed request
// overwrites a value that is on (SCRUM-2128). Next serializes it in props, unlike `undefined`.
export type ToggleValue = boolean | null;
export type ToggleValues = Record<string, ToggleValue>;

export type PendingRequest = {
  promise: Promise<ToggleValue> | Promise<ToggleValues>;
  timestamp: number;
  ttl: number;
};

// `ok` is false when `value` is the unresolved fallback of a failed request rather than an
// answer from the API; `rateLimited` marks a 429, which keeps the full TTL.
type ToggleResult<T> = {
  value: T;
  ok: boolean;
  rateLimited?: boolean;
};

interface FeatureToggleRequest {
  key: string;
  project: string;
  environment: string;
}

interface FeatureToggleResponse {
  enabled: boolean;
  key?: string;
  project?: string;
  environment?: string;
}

interface MultipleToggleRequest {
  keys: string[];
  project: string;
  environment: string;
}

interface MultipleToggleResponse {
  [key: string]: {
    enabled: boolean;
    key: string;
  };
}

interface CacheStatusResponse {
  cached: boolean;
  content_hash: string | null;
  timestamp: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Resolved-result cache TTL. Within this window, repeated checks for the same
// key set reuse the cached promise instead of hitting the API again. This is the
// main throttle relief: SSR (getServerSideProps) renders share one fetch per
// project+env+keys per window, collapsing per-request traffic to api.noping.com.
export const TOGGLE_CACHE_TTL = 60000; // 60 seconds
// A failed request is cached for a shorter window: long enough that a sustained outage costs one
// request per key set every 10s instead of one per page view, short enough that recovery shows fast.
// A 429 is the exception and keeps TOGGLE_CACHE_TTL: retrying sooner is what the API is refusing.
export const TOGGLE_ERROR_CACHE_TTL = 10000; // 10 seconds
const POLL_INTERVAL = 40000; // 40 seconds
const MAX_POLLING_FAILURES = 3; // Stop polling after 3 consecutive failures

// ============================================================================
// SHARED MAPS
// ============================================================================

export const pendingRequests = new Map<string, PendingRequest>();
// Last value the API actually answered per request key. Never cleared on cache invalidation: it
// is only read when a request fails, so a failure serves the last real answer instead of an
// unresolved `null`.
export const lastKnownToggleValues = new Map<string, ToggleValue | ToggleValues>();
let lastKnownHash: string | null = null;
let pollingIntervalId: NodeJS.Timeout | null = null;
let consecutiveFailures = 0;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Helper to check if we're running on server-side
function isServerSide(): boolean {
  return typeof window === 'undefined';
}

// Console helper - only logs on server-side
const serverConsole = {
  log: (...args: any[]) => {
    if (isServerSide()) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (isServerSide()) {
      // eslint-disable-next-line no-console
      console.error(...args);
    }
  },
  warn: (...args: any[]) => {
    if (isServerSide()) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  },
};

// Cache invalidation listeners
type CacheInvalidationListener = () => void;
const cacheInvalidationListeners = new Set<CacheInvalidationListener>();

export function onCacheInvalidated(listener: CacheInvalidationListener): () => void {
  cacheInvalidationListeners.add(listener);
  // Return unsubscribe function
  return () => {
    cacheInvalidationListeners.delete(listener);
  };
}

function notifyCacheInvalidation(): void {
  cacheInvalidationListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      serverConsole.error('[FeatureToggle Store] Error in cache invalidation listener:', error);
    }
  });
}

export function getProjectFromUrl(): string {
  const frontendUrl = process.env.NEXT_PUBLIC_FRONT_END_URL || '';
  if (frontendUrl.includes('localhost')) {
    return 'staging-site.noping.com';
  }
  if (frontendUrl.includes('staging-site.noping.com')) {
    return 'staging-site.noping.com';
  }
  if (frontendUrl.includes('noping.com')) {
    return 'noping.com';
  }
  return 'localhost';
}

export function getRequestKey(key: string, project: string, environment: string): string {
  return `${key}:${project}:${environment}`;
}

// ============================================================================
// FETCH FUNCTIONS (USING NATIVE FETCH API)
// ============================================================================

// Drops the keys the API could not answer, so merging the result never overwrites a known value.
export function resolvedToggles(values: ToggleValues): Record<string, boolean> {
  return Object.fromEntries(Object.entries(values).filter((entry): entry is [string, boolean] => entry[1] !== null));
}

function unresolvedToggles(keys: string[]): ToggleValues {
  const unresolvedResults: ToggleValues = {};
  for (const key of keys) {
    unresolvedResults[key] = null;
  }
  return unresolvedResults;
}

export async function fetchSingleToggle(key: string, project: string, environment: string, apiBaseUrl: string): Promise<ToggleResult<ToggleValue>> {
  try {
    const payload: FeatureToggleRequest = {
      key,
      project,
      environment,
    };

    const response = await fetch(`${apiBaseUrl}/feature-toggle/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      serverConsole.error(`Error checking feature toggle '${key}': ${response.status} ${response.statusText}.`);
      return { value: null, ok: false, rateLimited: response.status === 429 };
    }

    const data = (await response.json()) as FeatureToggleResponse;
    return { value: data.enabled || false, ok: true };
  } catch (error) {
    serverConsole.error(`Error checking feature toggle '${key}':`, error);
    return { value: null, ok: false };
  }
}

export async function fetchMultipleToggles(keysToFetch: string[], project: string, environment: string, apiBaseUrl: string): Promise<ToggleResult<ToggleValues>> {
  try {
    const payload: MultipleToggleRequest = {
      keys: keysToFetch,
      project,
      environment,
    };

    const response = await fetch(`${apiBaseUrl}/feature-toggle/check-multiple`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      serverConsole.error(`Error checking feature toggles: ${response.status} ${response.statusText}. Using fallback`);
      return { value: unresolvedToggles(keysToFetch), ok: false, rateLimited: response.status === 429 };
    }

    const data = (await response.json()) as MultipleToggleResponse;
    serverConsole.log('Feature toggle batch response data:', data);

    // Transform API response to Record<string, boolean>
    const batchResults: Record<string, boolean> = {};
    for (const key of keysToFetch) {
      const toggleData = data[key];
      batchResults[key] = toggleData?.enabled ?? false;
    }

    return { value: batchResults, ok: true };
  } catch (error) {
    serverConsole.error('Error checking feature toggles:', error, 'Using fallback');
    return { value: unresolvedToggles(keysToFetch), ok: false };
  }
}

// ============================================================================
// TTL CACHE (SHARED BY SSR AND CLIENT)
// ============================================================================

// Reuses the cached promise for `requestKey` while it is within its TTL, otherwise starts
// `request`. The TTL applies from the start, so concurrent callers share one in-flight request;
// once it settles, a failure (other than a 429) shortens the entry to TOGGLE_ERROR_CACHE_TTL and
// resolves to the last value the API answered, or to the unresolved `null`s on a cold failure.
export function cachedToggleRequest<T extends ToggleValue | ToggleValues>(requestKey: string, request: () => Promise<ToggleResult<T>>): Promise<T> {
  const pending = pendingRequests.get(requestKey);
  if (pending && Date.now() - pending.timestamp < pending.ttl) {
    return pending.promise as Promise<T>;
  }

  const entry: PendingRequest = { promise: Promise.resolve(false), timestamp: Date.now(), ttl: TOGGLE_CACHE_TTL };
  const promise = request().then((result) => {
    if (result.ok) {
      lastKnownToggleValues.set(requestKey, result.value);
      return result.value;
    }
    entry.ttl = result.rateLimited ? TOGGLE_CACHE_TTL : TOGGLE_ERROR_CACHE_TTL;
    return (lastKnownToggleValues.get(requestKey) as T | undefined) ?? result.value;
  });
  entry.promise = promise as PendingRequest['promise'];
  pendingRequests.set(requestKey, entry);

  return promise;
}

// ============================================================================
// BACKEND CACHE STATUS POLLING (AUTOMATIC INVALIDATION)
// ============================================================================

function stopPolling(): void {
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
}

async function checkCacheStatus(): Promise<void> {
  try {
    const project = getProjectFromUrl();
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL;

    if (!apiBaseUrl) {
      return;
    }

    const response = await fetch(`${apiBaseUrl}/feature-toggle/cache-status?project=${project}`);

    if (!response.ok) {
      consecutiveFailures += 1;

      if (consecutiveFailures >= MAX_POLLING_FAILURES) {
        stopPolling();
      }
      return;
    }

    const data = (await response.json()) as CacheStatusResponse;

    // Reset failure counter on successful response
    consecutiveFailures = 0;

    // Detect if backend data changed:
    // 1. Backend cache refreshed (cached: false) - means data was updated
    // 2. OR content_hash changed from what we know
    const currentHash = data.content_hash;
    const backendCacheInvalidated = data.cached === false;
    const hashChanged = lastKnownHash !== null && currentHash !== lastKnownHash;
    const shouldInvalidate = backendCacheInvalidated || hashChanged;

    if (shouldInvalidate) {
      pendingRequests.clear();
      serverConsole.log('[FeatureToggle Store] Backend cache changed, triggering re-fetch', {
        reason: backendCacheInvalidated ? 'backend cache refreshed' : 'hash changed',
        old: lastKnownHash,
        new: currentHash,
        backendCached: data.cached,
      });
      // Notify all listeners to re-fetch values
      notifyCacheInvalidation();
    }

    // Update last known hash (even if null)
    lastKnownHash = currentHash;
  } catch (error) {
    consecutiveFailures += 1;

    if (consecutiveFailures >= MAX_POLLING_FAILURES) {
      stopPolling();
    }
  }
}

// Start polling for cache status ONLY in client-side (browser)
// Server-side doesn't need polling as it's stateless per request
if (typeof window !== 'undefined') {
  pollingIntervalId = setInterval(checkCacheStatus, POLL_INTERVAL);
}
